import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';
import { QuickQuestions, QuickQuestionsSteps } from '@gelato-api-ui/core/quick-questions/types/quick-questions';
import { Question } from '@gelato-api-ui/core/quick-questions/types/question';
import { ErrorData } from '@api-ui-app/src/app/shared/lib/error-data';
import { ErrorDetails } from '@api-ui-app/src/app/shared/lib/error-details';
import { IsAlreadySellingOptions } from '@gelato-api-ui/core/quick-questions/constants/is-already-selling-options';
import {
  SellingProductsTodayOptions,
  SellingProductsTodayOptionsIds,
} from '@gelato-api-ui/core/quick-questions/constants/selling-products-today-options';
import { OnlinePresenceOptions } from '@gelato-api-ui/core/quick-questions/constants/online-presence-options';
import { FollowersOptions } from '@gelato-api-ui/core/quick-questions/constants/followers-options';
import { RevenueOptions } from '@gelato-api-ui/core/quick-questions/constants/revenue-options';
import { IndustryOptions } from '@gelato-api-ui/core/quick-questions/constants/industry-options';
import { EcommercePlatformOptions } from '@gelato-api-ui/core/quick-questions/constants/ecommerce-platform-options';
import { SellingSystemOptions } from '@gelato-api-ui/core/quick-questions/constants/selling-system-options';
import { QuickQuestionOptionsCollection } from '@gelato-api-ui/core/quick-questions/types/quick-question-options-collection';
import { RefererOptions } from '@gelato-api-ui/core/quick-questions/constants/referer-options';
import { ReviewSiteOptions } from '@gelato-api-ui/core/quick-questions/constants/review-site-options';
import { SocialMediaChannelOptions } from '../constants/social-media-channel-options';
import { RefererOptionIdsEnum } from '@gelato-api-ui/core/quick-questions/types/referer-option-ids.enum';
import { QuickQuestionCommonOptionsEnum } from '@gelato-api-ui/core/quick-questions/types/quick-question-common-options.enum';
import { PhoneNumberService } from '@gelato-api-ui/core/quick-questions/services/phone-number.service';
import { SuperpowerOptions } from '../constants/superpower-options';
import { EcommerceStoreYearlyRevenueOptions } from '../types/ecommerce-store-yearly-revenue';
import { CountryWithDialingCode } from '@gelato-api-ui/sdk/src/lib/shared/country-selector/types/country-selector';

@Injectable({ providedIn: 'root' })
export class QuickQuestionsService {
  valuesSeparator = ',';
  bigEcommerceStoreYearlyRevenueOptionValues = ['25k-50k', '50k-100k', '100k-500k', '>500k'];

  constructor(
    private readonly translateService: TranslateService,
    private readonly phoneNumberService: PhoneNumberService,
  ) {}

  fromQuestionnaireArray(questionnaire: Question[]): QuickQuestions {
    const data = questionnaire || [];

    const eCommercePlatformValue = this.findAnswer<string>(data, QuickQuestionsSteps.ecommercePlatform, '');

    const quickQuestions = new QuickQuestions(
      this.findAnswer<string>(data, QuickQuestionsSteps.sellingProductsToday, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.sellingSystem, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.customSellingSystem, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.onlinePresence, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.customOnlinePresence, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.followers, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.isAlreadySelling, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.revenue, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.ecommerceStoreYearlyRevenue, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.industry, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.customIndustry, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.superpower, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.customSuperpower, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.referer, ''),
      eCommercePlatformValue ? eCommercePlatformValue.split(this.valuesSeparator) : [],
      this.findAnswer<string>(data, QuickQuestionsSteps.storeLink, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.customEcommercePlatform, ''),
      this.findAnswer<boolean>(data, QuickQuestionsSteps.termsAndConditions, false),
      this.findAnswer<boolean>(data, QuickQuestionsSteps.receiveNews, true),
      this.findAnswer<string>(data, QuickQuestionsSteps.phoneNumber, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.phonePrefix, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.customReferer, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.influencer, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.podcast, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.reviewSite, ''),
      this.findAnswer<string>(data, QuickQuestionsSteps.customReviewSite, ''),
      null,
      this.findAnswer(data, QuickQuestionsSteps.socialMediaChannel, ''),
      this.findAnswer(data, QuickQuestionsSteps.customSocialMediaChannel, ''),
    );

    return this.cleanupFields(quickQuestions);
  }

  toQuestionnaireArray(quickQuestions: QuickQuestions): Question[] {
    return !quickQuestions
      ? []
      : [
          {
            question: QuickQuestionsSteps.sellingProductsToday,
            answer: quickQuestions.sellingProductsToday || '',
          },
          {
            question: QuickQuestionsSteps.sellingSystem,
            answer: quickQuestions.sellingSystem || '',
          },
          {
            question: QuickQuestionsSteps.customSellingSystem,
            answer: quickQuestions.customSellingSystem || '',
          },
          {
            question: QuickQuestionsSteps.onlinePresence,
            answer: quickQuestions.onlinePresence || '',
          },
          {
            question: QuickQuestionsSteps.customOnlinePresence,
            answer: quickQuestions.customOnlinePresence || '',
          },
          {
            question: QuickQuestionsSteps.followers,
            answer: quickQuestions.followers || '',
          },
          {
            question: QuickQuestionsSteps.isAlreadySelling,
            answer: quickQuestions.isAlreadySelling || '',
          },
          {
            question: QuickQuestionsSteps.industry,
            answer: quickQuestions.industry || '',
          },
          {
            question: QuickQuestionsSteps.customIndustry,
            answer: quickQuestions.customIndustry || '',
          },
          {
            question: QuickQuestionsSteps.ecommerceStoreYearlyRevenue,
            answer: quickQuestions.ecommerceStoreYearlyRevenue || '',
          },
          {
            question: QuickQuestionsSteps.ecommercePlatform,
            answer: (quickQuestions.ecommercePlatform || []).join(this.valuesSeparator),
          },
          {
            question: QuickQuestionsSteps.storeLink,
            answer: quickQuestions.storeLink || '',
          },
          {
            question: QuickQuestionsSteps.customEcommercePlatform,
            answer: quickQuestions.customEcommercePlatform || '',
          },
          {
            question: QuickQuestionsSteps.phonePrefix,
            answer: quickQuestions.phonePrefix || '',
          },
          {
            question: QuickQuestionsSteps.phoneNumber,
            answer: quickQuestions.phoneNumber || '',
          },
          {
            question: QuickQuestionsSteps.termsAndConditions,
            answer: quickQuestions.termsAndConditions,
          },
          {
            question: QuickQuestionsSteps.receiveNews,
            answer: quickQuestions.receiveNews,
          },
          {
            question: QuickQuestionsSteps.superpower,
            answer: quickQuestions.superpower || '',
          },
          {
            question: QuickQuestionsSteps.customSuperpower,
            answer: quickQuestions.customSuperpower || '',
          },
          {
            question: QuickQuestionsSteps.referer,
            answer: quickQuestions.referer || '',
          },
          {
            question: QuickQuestionsSteps.customReferer,
            answer: quickQuestions.customReferer || '',
          },
          {
            question: QuickQuestionsSteps.influencer,
            answer: quickQuestions.influencer || '',
          },
          {
            question: QuickQuestionsSteps.podcast,
            answer: quickQuestions.podcast || '',
          },
          {
            question: QuickQuestionsSteps.reviewSite,
            answer: quickQuestions.reviewSite || '',
          },
          {
            question: QuickQuestionsSteps.customReviewSite,
            answer: quickQuestions.customReviewSite || '',
          },
          {
            question: QuickQuestionsSteps.socialMediaChannel,
            answer: quickQuestions.socialMediaChannel || '',
          },
          {
            question: QuickQuestionsSteps.customSocialMediaChannel,
            answer: quickQuestions.customSocialMediaChannel || '',
          },
        ];
  }

  validate(
    inQuickQuestions: QuickQuestions,
    activeStep: QuickQuestionsSteps,
    isLastStep: boolean,
    checkMainFieldOnly: boolean = false,
  ): ErrorData {
    const translations = this.translateService.instant([
      'txt_form_validation_general_error_message',
      'txt_form_validation_blank_field_message',
    ]);
    const quickQuestions: QuickQuestions = this.cleanupFields(inQuickQuestions);

    if (!quickQuestions) {
      return new ErrorData('', translations['txt_form_validation_general_error_message'], []);
    }

    const mandatoryFields = this.getMandatoryFields(quickQuestions, activeStep, isLastStep, checkMainFieldOnly);

    const emptyFields = R.filter(
      (fieldName: string): boolean =>
        !quickQuestions[fieldName] || R.isNil(quickQuestions[fieldName]) || R.isEmpty(quickQuestions[fieldName]),
      mandatoryFields,
    );

    const details: ErrorDetails[] = R.map(
      (fieldName: string): ErrorDetails =>
        new ErrorDetails(fieldName, translations['txt_form_validation_blank_field_message']),
      emptyFields,
    );

    if (!details.length) {
      return null;
    }

    return new ErrorData('', translations['txt_form_validation_general_error_message'], details);
  }

  private getMandatoryFields(
    quickQuestions: QuickQuestions,
    activeStep: QuickQuestionsSteps,
    isLastStep: boolean,
    checkMainFieldOnly: boolean,
  ): string[] {
    let mandatoryFields: string[] = [
      QuickQuestionsSteps.sellingProductsToday,
      QuickQuestionsSteps.followers,
      QuickQuestionsSteps.isAlreadySelling,
      QuickQuestionsSteps.ecommerceStoreYearlyRevenue,
      QuickQuestionsSteps.superpower,
      QuickQuestionsSteps.referer,
      QuickQuestionsSteps.reviewSite,
    ];

    // Add 2nd step
    if (quickQuestions.sellingProductsToday === 'sell_merch_to_my_followers') {
      mandatoryFields.push(QuickQuestionsSteps.onlinePresence);
    } else if (quickQuestions.sellingProductsToday !== 'sell_merch_to_my_followers') {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.onlinePresence);
    }

    // Add 3rd step
    if (!!quickQuestions.onlinePresence) {
      mandatoryFields.push(QuickQuestionsSteps.followers);
    } else if (!quickQuestions.onlinePresence) {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.followers);
    }

    // Add 4th step
    if (!!quickQuestions.followers) {
      mandatoryFields.push(QuickQuestionsSteps.isAlreadySelling);
    } else if (!quickQuestions.followers) {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.isAlreadySelling);
    }

    // Add 5th step
    if (quickQuestions.ecommercePlatform?.length > 0) {
      mandatoryFields.push(QuickQuestionsSteps.ecommerceStoreYearlyRevenue);
    } else if (!quickQuestions.ecommercePlatform || !quickQuestions.ecommercePlatform.length) {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.ecommerceStoreYearlyRevenue);
    }

    // Add 6th step
    if (
      quickQuestions.isAlreadySelling ||
      quickQuestions.ecommerceStoreYearlyRevenue ||
      !this.isBigEcommerceStoreYearlyRevenueOptionValue(quickQuestions.ecommerceStoreYearlyRevenue) ||
      (quickQuestions.phoneNumber && quickQuestions.phonePrefix) ||
      (quickQuestions.sellingProductsToday !== 'sell_merch_to_my_followers' &&
        quickQuestions.sellingProductsToday !== 'grow_my_online_business')
    ) {
      mandatoryFields.push(QuickQuestionsSteps.industry);
    } else if (
      !quickQuestions.isAlreadySelling ||
      !quickQuestions.ecommerceStoreYearlyRevenue ||
      !quickQuestions.phoneNumber ||
      quickQuestions.sellingProductsToday === 'sell_merch_to_my_followers' ||
      quickQuestions.sellingProductsToday === 'grow_my_online_business'
    ) {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.industry);
    }

    if (!!quickQuestions.onlinePresence && quickQuestions.onlinePresence.indexOf('Other') !== -1) {
      mandatoryFields.push(QuickQuestionsSteps.customOnlinePresence);
    }

    // Add 7th step

    if (quickQuestions.superpower?.indexOf(QuickQuestionCommonOptionsEnum.Other) !== -1) {
      mandatoryFields.push(QuickQuestionsSteps.customSuperpower);
    }

    // Add 8th step
    if (quickQuestions.referer === RefererOptionIdsEnum.Influencer) {
      mandatoryFields.push(QuickQuestionsSteps.influencer);
    } else {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.influencer);
    }

    if (quickQuestions.referer === RefererOptionIdsEnum.Podcast) {
      mandatoryFields.push(QuickQuestionsSteps.podcast);
    } else {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.podcast);
    }

    if (quickQuestions.referer?.indexOf(QuickQuestionCommonOptionsEnum.Other) !== -1) {
      mandatoryFields.push(QuickQuestionsSteps.customReferer);
    }

    // Add 9th step
    if (quickQuestions.referer === RefererOptionIdsEnum.ReviewSite) {
      mandatoryFields.push(QuickQuestionsSteps.reviewSite);
    } else {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.reviewSite);
    }

    // Add 10th step
    if (quickQuestions.referer === RefererOptionIdsEnum.SocialMedia) {
      mandatoryFields.push(QuickQuestionsSteps.socialMediaChannel);
    } else {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.socialMediaChannel);
    }

    if (quickQuestions.socialMediaChannel === QuickQuestionCommonOptionsEnum.Other) {
      mandatoryFields.push(QuickQuestionsSteps.customSocialMediaChannel);
    } else {
      mandatoryFields = mandatoryFields.filter(field => field !== QuickQuestionsSteps.customSocialMediaChannel);
    }

    if (quickQuestions.reviewSite?.indexOf('other') !== -1) {
      mandatoryFields.push(QuickQuestionsSteps.customReviewSite);
    }

    if (!!quickQuestions.ecommercePlatform && quickQuestions.ecommercePlatform.indexOf('other') !== -1) {
      mandatoryFields.push(QuickQuestionsSteps.customEcommercePlatform);
    }

    if (!quickQuestions.termsAndConditions) {
      mandatoryFields.push(QuickQuestionsSteps.termsAndConditions);
    }

    const fieldsToCheck = this.getFieldsRelevantToActiveStep(activeStep, isLastStep, checkMainFieldOnly);

    return mandatoryFields.filter(fieldName => fieldsToCheck.includes(fieldName));
  }

  private getFieldsRelevantToActiveStep(
    activeStep: QuickQuestionsSteps,
    isLastStep: boolean,
    mainFieldOnly: boolean,
  ): string[] {
    const relevantFields = [];

    switch (activeStep) {
      case QuickQuestionsSteps.sellingProductsToday:
        relevantFields.push(QuickQuestionsSteps.sellingProductsToday);
        break;
      case QuickQuestionsSteps.onlinePresence:
        relevantFields.push(QuickQuestionsSteps.onlinePresence);

        if (!mainFieldOnly) {
          relevantFields.push(QuickQuestionsSteps.customOnlinePresence);
        }
        break;
      case QuickQuestionsSteps.followers:
        relevantFields.push(QuickQuestionsSteps.followers);
        break;
      case QuickQuestionsSteps.isAlreadySelling:
        relevantFields.push(QuickQuestionsSteps.isAlreadySelling);
        break;
      case QuickQuestionsSteps.ecommercePlatform:
        relevantFields.push(QuickQuestionsSteps.ecommercePlatform);

        if (!mainFieldOnly) {
          relevantFields.push(QuickQuestionsSteps.customEcommercePlatform);
        }
        break;
      case QuickQuestionsSteps.storeLink:
        relevantFields.push(QuickQuestionsSteps.storeLink);
        break;
      case QuickQuestionsSteps.ecommerceStoreYearlyRevenue:
        relevantFields.push(QuickQuestionsSteps.ecommerceStoreYearlyRevenue);
        break;
      case QuickQuestionsSteps.phoneNumber:
        relevantFields.push(QuickQuestionsSteps.phoneNumber);
        relevantFields.push(QuickQuestionsSteps.phonePrefix);
        break;
      case QuickQuestionsSteps.industry:
        relevantFields.push(QuickQuestionsSteps.industry);
        break;
      case QuickQuestionsSteps.superpower:
        relevantFields.push(QuickQuestionsSteps.superpower);

        if (!mainFieldOnly) {
          relevantFields.push(QuickQuestionsSteps.customSuperpower);
        }
        break;
      case QuickQuestionsSteps.referer:
        relevantFields.push(QuickQuestionsSteps.referer);

        if (!mainFieldOnly) {
          relevantFields.push(QuickQuestionsSteps.customReferer);
        }
        break;
      case QuickQuestionsSteps.influencer:
        relevantFields.push(QuickQuestionsSteps.influencer);
        break;
      case QuickQuestionsSteps.podcast:
        relevantFields.push(QuickQuestionsSteps.podcast);
        break;
      case QuickQuestionsSteps.reviewSite:
        relevantFields.push(QuickQuestionsSteps.reviewSite);

        if (!mainFieldOnly) {
          relevantFields.push(QuickQuestionsSteps.customReviewSite);
        }
        break;
      case QuickQuestionsSteps.socialMediaChannel:
        relevantFields.push(QuickQuestionsSteps.socialMediaChannel);

        if (!mainFieldOnly) {
          relevantFields.push(QuickQuestionsSteps.customSocialMediaChannel);
        }
        break;

      default:
    }

    if (isLastStep && !mainFieldOnly) {
      relevantFields.push(QuickQuestionsSteps.termsAndConditions);
    }

    return relevantFields;
  }

  getOptions(): QuickQuestionOptionsCollection {
    return {
      [QuickQuestionsSteps.revenue]: RevenueOptions,
      [QuickQuestionsSteps.ecommerceStoreYearlyRevenue]: RevenueOptions,
      [QuickQuestionsSteps.industry]: IndustryOptions,
      [QuickQuestionsSteps.ecommercePlatform]: EcommercePlatformOptions,
      [QuickQuestionsSteps.sellingSystem]: SellingSystemOptions,
      [QuickQuestionsSteps.sellingProductsToday]: SellingProductsTodayOptions,
      [QuickQuestionsSteps.isAlreadySelling]: IsAlreadySellingOptions,
      [QuickQuestionsSteps.onlinePresence]: OnlinePresenceOptions,
      [QuickQuestionsSteps.followers]: FollowersOptions,
      [QuickQuestionsSteps.superpower]: SuperpowerOptions,
      [QuickQuestionsSteps.referer]: RefererOptions,
      [QuickQuestionsSteps.reviewSite]: ReviewSiteOptions,
      [QuickQuestionsSteps.socialMediaChannel]: SocialMediaChannelOptions,
    };
  }

  isStepAvailable(stepName: QuickQuestionsSteps, quickQuestions: QuickQuestions): boolean {
    switch (stepName) {
      case QuickQuestionsSteps.sellingProductsToday:
      case QuickQuestionsSteps.superpower:
      case QuickQuestionsSteps.referer:
        return true;
      case QuickQuestionsSteps.onlinePresence:
        return quickQuestions.sellingProductsToday === SellingProductsTodayOptionsIds.SellMerchToMyFollowers;
      case QuickQuestionsSteps.followers:
        return Boolean(quickQuestions.onlinePresence);
      case QuickQuestionsSteps.isAlreadySelling:
        return Boolean(quickQuestions.followers);
      case QuickQuestionsSteps.ecommercePlatform:
        return quickQuestions.sellingProductsToday === SellingProductsTodayOptionsIds.GrowMyOnlineBusiness;
      case QuickQuestionsSteps.storeLink:
      case QuickQuestionsSteps.ecommerceStoreYearlyRevenue:
        return Boolean(quickQuestions.ecommercePlatform?.length > 0);
      case QuickQuestionsSteps.influencer:
        return quickQuestions.referer === RefererOptionIdsEnum.Influencer;
      case QuickQuestionsSteps.podcast:
        return quickQuestions.referer === RefererOptionIdsEnum.Podcast;
      case QuickQuestionsSteps.reviewSite:
        return quickQuestions.referer === RefererOptionIdsEnum.ReviewSite;
      case QuickQuestionsSteps.phoneNumber:
        return Boolean(
          [
            EcommerceStoreYearlyRevenueOptions.From50To100k,
            EcommerceStoreYearlyRevenueOptions.From100To500k,
            EcommerceStoreYearlyRevenueOptions.Above500k,
          ].includes(quickQuestions?.ecommerceStoreYearlyRevenue as EcommerceStoreYearlyRevenueOptions),
        );
      case QuickQuestionsSteps.industry:
        const sellingProductIds = [
          SellingProductsTodayOptionsIds.StartFirstEcommerceBusiness,
          SellingProductsTodayOptionsIds.BecomeAnAffiliate,
          SellingProductsTodayOptionsIds.OrderCustomerProductForMyself,
          SellingProductsTodayOptionsIds.JustPlayingAround,
          SellingProductsTodayOptionsIds.GrowMyOnlineBusiness,
        ];

        const isPhoneNumberPopulated = Boolean(quickQuestions.phoneNumber && quickQuestions.phonePrefix);

        return (
          isPhoneNumberPopulated ||
          Boolean(quickQuestions.isAlreadySelling) ||
          sellingProductIds.includes(quickQuestions.sellingProductsToday as SellingProductsTodayOptionsIds)
        );
      case QuickQuestionsSteps.socialMediaChannel:
        return quickQuestions.referer === RefererOptionIdsEnum.SocialMedia;
      default:
        return false;
    }
  }

  getAvailableSteps(quickQuestions: QuickQuestions): QuickQuestionsSteps[] {
    // Keep the order to display the question steps correctly
    const allSteps = [
      QuickQuestionsSteps.sellingProductsToday,
      QuickQuestionsSteps.superpower,
      QuickQuestionsSteps.onlinePresence,
      QuickQuestionsSteps.followers,
      QuickQuestionsSteps.isAlreadySelling,
      QuickQuestionsSteps.ecommercePlatform,
      QuickQuestionsSteps.storeLink,
      QuickQuestionsSteps.ecommerceStoreYearlyRevenue,
      QuickQuestionsSteps.phoneNumber,
      QuickQuestionsSteps.industry,
      QuickQuestionsSteps.referer,
      QuickQuestionsSteps.influencer,
      QuickQuestionsSteps.podcast,
      QuickQuestionsSteps.reviewSite,
      QuickQuestionsSteps.socialMediaChannel,
    ];

    return allSteps.filter((loopStep: QuickQuestionsSteps): boolean => this.isStepAvailable(loopStep, quickQuestions));
  }

  setAnswer(
    quickQuestions: QuickQuestions,
    countries: CountryWithDialingCode[],
    stepName: QuickQuestionsSteps,
    value: string & string[],
  ): QuickQuestions {
    let updatedQuickQuestions: QuickQuestions;

    if (stepName === QuickQuestionsSteps.phoneNumber && quickQuestions.phonePrefix) {
      updatedQuickQuestions = {
        ...quickQuestions,
        [QuickQuestionsSteps.phoneNumber]: this.phoneNumberService.getFormattedPhoneNumberByPhonePrefix(
          countries,
          quickQuestions.phonePrefix,
          value as string,
          false,
        ),
      };
    } else {
      updatedQuickQuestions = {
        ...quickQuestions,
        [stepName]: value,
      };
    }

    switch (stepName) {
      case QuickQuestionsSteps.sellingProductsToday:
        return {
          ...new QuickQuestions(),
          termsAndConditions: quickQuestions.termsAndConditions,
          receiveNews: quickQuestions.receiveNews,
          [stepName]: value,
        };
      case QuickQuestionsSteps.onlinePresence:
        return this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.followers, null);
      case QuickQuestionsSteps.followers:
        return this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.isAlreadySelling, null);
      case QuickQuestionsSteps.isAlreadySelling:
        return this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.ecommercePlatform, null);
      case QuickQuestionsSteps.ecommercePlatform:
        return this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.storeLink, null);
      case QuickQuestionsSteps.storeLink:
        return this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.ecommerceStoreYearlyRevenue, null);
      case QuickQuestionsSteps.ecommerceStoreYearlyRevenue:
        updatedQuickQuestions = this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.phoneNumber, null);
        updatedQuickQuestions = this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.industry, null);

        return updatedQuickQuestions;
      case QuickQuestionsSteps.phoneNumber:
        return this.setAnswer(updatedQuickQuestions, countries, QuickQuestionsSteps.industry, null);
      default:
        return updatedQuickQuestions;
    }
  }

  private findAnswer<T>(data: Question[], question: QuickQuestionsSteps, defaultAnswer: T): T {
    return (data.find(q => q.question === question)?.answer || defaultAnswer) as T;
  }

  private isBigEcommerceStoreYearlyRevenueOptionValue(ecommerceStoreYearlyRevenue: string) {
    return this.bigEcommerceStoreYearlyRevenueOptionValues.includes(ecommerceStoreYearlyRevenue);
  }

  private cleanupFields(inQuickQuestions: QuickQuestions) {
    const quickQuestions: QuickQuestions = { ...inQuickQuestions };

    if (quickQuestions.sellingProductsToday !== 'selling_with_different_system') {
      quickQuestions.sellingSystem = '';
      quickQuestions.customSellingSystem = '';
    }

    if (quickQuestions.sellingSystem !== QuickQuestionCommonOptionsEnum.Other) {
      quickQuestions.customSellingSystem = '';
    }

    if (quickQuestions.superpower !== QuickQuestionCommonOptionsEnum.Other) {
      quickQuestions.customSuperpower = '';
    }

    if (quickQuestions.referer !== QuickQuestionCommonOptionsEnum.Other) {
      quickQuestions.customReferer = '';
    } else {
      quickQuestions.influencer = '';
      quickQuestions.reviewSite = '';
      quickQuestions.podcast = '';
    }

    if (quickQuestions.referer !== RefererOptionIdsEnum.Influencer) {
      quickQuestions.influencer = '';
    }

    if (quickQuestions.referer !== RefererOptionIdsEnum.Podcast) {
      quickQuestions.podcast = '';
    }

    if (quickQuestions.referer !== RefererOptionIdsEnum.ReviewSite) {
      quickQuestions.reviewSite = '';
    }

    if (quickQuestions.referer !== RefererOptionIdsEnum.SocialMedia) {
      quickQuestions.socialMediaChannel = '';
      quickQuestions.customSocialMediaChannel = '';
    }

    if (quickQuestions.reviewSite !== QuickQuestionCommonOptionsEnum.Other) {
      quickQuestions.customReviewSite = '';
    }

    if (!!quickQuestions.onlinePresence && quickQuestions.onlinePresence.indexOf('Other') === -1) {
      quickQuestions.customOnlinePresence = '';
    }

    if (!!quickQuestions.industry && quickQuestions.industry === QuickQuestionCommonOptionsEnum.Other) {
      quickQuestions.customIndustry = '';
    }

    if (!quickQuestions?.ecommercePlatform?.includes(QuickQuestionCommonOptionsEnum.Other)) {
      quickQuestions.customEcommercePlatform = '';
    }

    if (quickQuestions.socialMediaChannel !== QuickQuestionCommonOptionsEnum.Other) {
      quickQuestions.customSocialMediaChannel = '';
    }

    return quickQuestions;
  }
}
